import type { BusinessRiskMetrics, Experiment, RunDetail } from '../api/runs';
import { formatPercentage } from './percentage';

export const formatPercent = formatPercentage;

export const formatMultiplier = (value?: number | null, digits = 2) =>
  typeof value === 'number' && Number.isFinite(value)
    ? `${value.toFixed(digits)}×`
    : 'N/A';

export const formatRunType = (value?: string | null) => {
  const normalized = String(value || '').toLowerCase();
  if (normalized === 'functional') {
    return 'Functional';
  }
  if (normalized === 'chaos') {
    return 'Chaos';
  }
  return value || 'N/A';
};

export const getExperimentRiskScore = (experiment: Experiment | any) => {
  const breakdown = experiment?.breakdown ?? {};
  const reasonBreakdown = experiment?.reasons?.breakdown ?? {};
  const candidates = [
    experiment?.risk_score,
    experiment?.p_initial,
    breakdown?.p_initial,
    breakdown?.static_risk,
    reasonBreakdown?.p_initial,
  ];

  for (const candidate of candidates) {
    const value = Number(candidate);
    if (Number.isFinite(value)) {
      return value;
    }
  }

  return null;
};

export const getExperimentExecutionValue = (experiment: Experiment | any) => {
  const breakdown = experiment?.breakdown ?? {};
  const reasons = experiment?.reasons ?? {};
  const reasonBreakdown = reasons?.breakdown ?? {};
  const runtime = reasons?.runtime_modulation ?? {};
  const candidates = [
    experiment?.execution_value,
    experiment?.final_execution_value,
    breakdown?.execution_value,
    breakdown?.final_execution_value,
    runtime?.final_value,
    reasonBreakdown?.execution_value,
    reasonBreakdown?.final_execution_value,
  ];
  const riskScore = getExperimentRiskScore(experiment);

  for (const candidate of candidates) {
    const value = Number(candidate);
    if (
      Number.isFinite(value) &&
      (riskScore === null || Math.abs(value - riskScore) > 0.0001)
    ) {
      return value;
    }
  }

  return null;
};

export const deriveBusinessRiskMetrics = (
  detail?: RunDetail | null
): BusinessRiskMetrics => {
  if (!detail) {
    return {};
  }

  const backendMetrics = detail.business_metrics ?? {};
  const riskCoverage =
    backendMetrics.risk_coverage ?? detail.risk_coverage ?? undefined;
  const testBudget =
    backendMetrics.test_budget ?? detail.test_coverage ?? undefined;
  const selectedRisk =
    backendMetrics.selected_risk ?? detail.selected_risk_sum ?? undefined;
  const totalRisk = backendMetrics.total_risk ?? detail.risk_total ?? undefined;

  const residualRisk =
    backendMetrics.residual_risk ??
    (typeof riskCoverage === 'number' ? Math.max(0, 100 - riskCoverage) : undefined);
  const testsReduced =
    backendMetrics.tests_reduced ??
    detail.reduction_percentage ??
    (typeof testBudget === 'number' ? Math.max(0, 100 - testBudget) : undefined);
  const riskEfficiency =
    backendMetrics.risk_efficiency ??
    detail.risk_efficiency ??
    (typeof riskCoverage === 'number' && typeof testBudget === 'number' && testBudget > 0
      ? riskCoverage / testBudget
      : undefined);
  const highBandScoreMass =
    backendMetrics.high_risk_coverage ??
    backendMetrics.high_captured_pct ??
    detail.high_risk_coverage ??
    undefined;
  const pair = (covered?: number | null) =>
    typeof covered === 'number' && Number.isFinite(covered)
      ? {
          covered_pct: Math.max(0, Math.min(100, covered)),
          uncovered_pct: Math.max(0, 100 - Math.max(0, Math.min(100, covered))),
        }
      : { covered_pct: undefined, uncovered_pct: undefined };
  const riskCoverageSummary = backendMetrics.risk_coverage_summary ?? {
    overall: pair(riskCoverage),
    high: pair(highBandScoreMass),
    medium: pair(backendMetrics.medium_captured_pct),
    low: pair(backendMetrics.low_captured_pct),
  };

  return {
    ...backendMetrics,
    observed_risk: backendMetrics.observed_risk ?? (totalRisk ? 100 : undefined),
    total_risk: totalRisk,
    selected_risk: selectedRisk,
    risk_coverage: riskCoverage,
    residual_risk: residualRisk,
    top_risk_coverage:
      backendMetrics.top_risk_coverage ?? undefined,
    high_risk_coverage:
      highBandScoreMass,
    high_captured_pct:
      backendMetrics.high_captured_pct ??
      backendMetrics.high_risk_coverage ??
      undefined,
    medium_captured_pct: backendMetrics.medium_captured_pct ?? undefined,
    low_captured_pct: backendMetrics.low_captured_pct ?? undefined,
    high_residual_pct: backendMetrics.high_residual_pct ?? undefined,
    medium_residual_pct: backendMetrics.medium_residual_pct ?? undefined,
    low_residual_pct: backendMetrics.low_residual_pct ?? undefined,
    risk_coverage_summary: riskCoverageSummary,
    dependency_aware_tests_added:
      backendMetrics.dependency_aware_tests_added ??
      detail.dependency_aware_tests_added ??
      undefined,
    test_budget: testBudget,
    tests_reduced: testsReduced,
    risk_efficiency: riskEfficiency,
  };
};

export const businessReason = (experiment: Experiment, testType: string) => {
  if (testType !== 'functional') {
    return null;
  }

  const risk = getExperimentRiskScore(experiment);
  const riskText = risk === null ? 'N/A' : formatPercentage(risk * 100);
  const origin = (experiment.final_origin ?? experiment.origin ?? '').toLowerCase();
  const breakdown = (experiment as any).breakdown ?? {};

  if (breakdown.commit_affected || breakdown.commit_impact > 0) {
    return `High execution value. Covers a changed code path with dynamic risk signal ${riskText}.`;
  }

  if (origin.includes('entang')) {
    return `Correlated impact path. Covers service behavior linked to higher-risk tests; dynamic risk signal ${riskText}.`;
  }

  if (origin.includes('uncert')) {
    return `High information value. Reduces uncertainty in a medium-risk area; dynamic risk signal ${riskText}.`;
  }

  return `High execution value. Covers a service with strong dynamic risk signal ${riskText}.`;
};
